import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Heart, Sparkles, Star, User, Calendar, Sun, Compass, ChevronDown, ChevronUp, X, Lock, UserPlus } from 'lucide-react';
import { NUMBER_DESCRIPTIONS, getDailyAdvice, getCategoryDescription, LIFE_PATH_DESCRIPTIONS, SOUL_URGE_DESCRIPTIONS, EXPRESSION_DESCRIPTIONS, PERSONALITY_DESCRIPTIONS } from '@/utils/numerologyDescriptions';
import { calculateExpression, calculateSoul, calculatePersonality, calculateDaily } from '@/utils/numerologyCalculator';
import { calculateChineseZodiac } from '@/utils/chineseZodiac';
import { CalculationDialog } from '@/components/CalculationDialog';
import { EmailSubscription } from '@/components/EmailSubscription';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { generateEssence } from '@/utils/essenceGenerator';
import { explainLifePath, explainExpression, explainSoul, explainPersonality } from '@/utils/calculationExplainer';
import PythagorasSquare from '@/components/PythagorasSquare';
import VIPModal from '@/components/VIPModal';
import VIPSuccessModal from '@/components/VIPSuccessModal';
import { SoulArchiveBlock } from '@/components/NumaxisBlocks';
import UserMenu from '@/components/UserMenu';
import { useAuth } from '@/context/AuthContext';
import { getGreeting } from '@/utils/localization';
import Footer from '@/components/Footer';
import DailyEnergyAnalysis from '@/components/DailyEnergyAnalysis';
import AuthModal from '@/components/AuthModal';

// Guest calculation tracking
const GUEST_CALC_KEY = 'guestCalculations';
const MAX_GUEST_CALCULATIONS = 2;

const getGuestCalculations = () => {
  try {
    const stored = localStorage.getItem(GUEST_CALC_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addGuestCalculation = (firstName, lastName) => {
  const calcs = getGuestCalculations();
  const nameKey = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
  if (!calcs.includes(nameKey)) {
    calcs.push(nameKey);
    localStorage.setItem(GUEST_CALC_KEY, JSON.stringify(calcs));
  }
  return calcs.length;
};

const isGuestLimitReached = () => {
  return getGuestCalculations().length >= MAX_GUEST_CALCULATIONS;
};

// Guest Registration Popup Component (extracted for React best practices)
const GuestRegistrationPopup = ({ isVisible, isAuthenticated, onClose, onRegister }) => {
  if (!isVisible || isAuthenticated) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-mystical max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="guest-popup-close"
        >
          <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
        </button>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500/40">
            <UserPlus className="w-8 h-8 text-amber-400" />
          </div>
          
          <h3 className="font-display text-xl text-amber-400 mb-3">
            Patiko? 🌟
          </h3>
          
          <p className="text-foreground/80 mb-6 leading-relaxed">
            Užsiregistruok nemokamai ir gauk dar 7 vardų paskaičiavimus, savo vardo analizę bei asmenines prognozes!
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={onRegister}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold py-5"
              data-testid="guest-popup-register"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Registruotis nemokamai
            </Button>
            
            <button
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="guest-popup-later"
            >
              Gal vėliau
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default function ResultsPage() {
  const navigate = useNavigate();
  const { user, isVIP: authIsVIP, loading, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [isVIP, setIsVIP] = useState(false);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [guestCalcCount, setGuestCalcCount] = useState(0);
  const [showGuestPopup, setShowGuestPopup] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const guestPopupTimerRef = useRef(null);

  // Sync VIP status from auth context - CRITICAL: wait for loading to complete
  useEffect(() => {
    if (!loading) {
      // Set VIP from auth context OR from user object directly
      const vipStatus = authIsVIP || (user && user.isVIP);
      setIsVIP(vipStatus);
    }
  }, [authIsVIP, loading, user]);

  // 40-second popup timer for guests
  useEffect(() => {
    // Only start timer for unauthenticated guests with data loaded
    if (!loading && !isAuthenticated && data) {
      guestPopupTimerRef.current = setTimeout(() => {
        setShowGuestPopup(true);
      }, 40000); // 40 seconds
    }
    
    // Cleanup timer on unmount or when user becomes authenticated
    return () => {
      if (guestPopupTimerRef.current) {
        clearTimeout(guestPopupTimerRef.current);
      }
    };
  }, [loading, isAuthenticated, data]);

  useEffect(() => {
    // Wait for auth loading to complete
    if (loading) return;
    
    // PRIORITY: Always check localStorage first (for "Skaičiuoti iš naujo" functionality)
    const storedData = localStorage.getItem('numerologyData');
    
    if (storedData) {
      const parsed = JSON.parse(storedData);
      // Ensure we have all calculated numbers
      if (!parsed.expression || !parsed.soul || !parsed.personality) {
        const fullName = `${parsed.firstName} ${parsed.lastName}`;
        parsed.expression = calculateExpression(fullName);
        parsed.soul = calculateSoul(fullName);
        parsed.personality = calculatePersonality(fullName);
        parsed.daily = calculateDaily(parsed.day, parsed.month, parsed.year);
        localStorage.setItem('numerologyData', JSON.stringify(parsed));
      }
      setData(parsed);
      
      // Track guest calculations
      if (!isAuthenticated) {
        const count = addGuestCalculation(parsed.firstName, parsed.lastName);
        setGuestCalcCount(count);
      }
      return;
    }
    
    // Fallback: If no localStorage, check if user is logged in with data
    if (user && user.day && user.month && user.year && user.firstName && user.lastName) {
      const fullName = `${user.firstName} ${user.lastName}`;
      const calculatedData = {
        firstName: user.firstName,
        lastName: user.lastName,
        day: user.day,
        month: user.month,
        year: user.year,
        lifePath: user.lifePath,
        gender: user.gender || null,
        expression: calculateExpression(fullName),
        soul: calculateSoul(fullName),
        personality: calculatePersonality(fullName),
        daily: calculateDaily(user.day, user.month, user.year)
      };
      setData(calculatedData);
      localStorage.setItem('numerologyData', JSON.stringify(calculatedData));
      return;
    }
    
    // No data found - redirect to calculator
    toast.error('Duomenų nerasta. Prašome pirmiau užpildyti formą.');
    navigate('/skaiciuoti');
  }, [navigate, user, loading, isAuthenticated]);

  const handleVIPSuccess = (email) => {
    setIsVIP(true);
    setShowSuccessModal(true);
  };

  const handleCopy = (text, label) => {
    copyToClipboard(text, label);
  };

  if (!data) return null;

  // Check if content should be locked for guests (not authenticated and not VIP)
  const isGuest = !isAuthenticated;
  const shouldLockContent = isGuest && !isVIP;

  // Locked card component for guests
  const renderLockedCard = (title, icon, description) => {
    return (
      <Card 
        className="card-mystical p-6 shadow-card border-border/50 h-full flex flex-col relative overflow-hidden cursor-pointer"
        onClick={() => setShowAuthModal(true)}
        data-testid={`locked-card-${title}`}
      >
        {/* Locked overlay - solid dark background */}
        <div className="absolute inset-0 bg-slate-950 z-10 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <h3 className="font-semibold text-lg text-foreground mb-2">{title}</h3>
          <p className="text-sm text-muted-foreground text-center px-4 mb-4">{description}</p>
          <Button 
            className="bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-black font-bold"
            onClick={(e) => {
              e.stopPropagation();
              setShowAuthModal(true);
            }}
          >
            <Lock className="w-4 h-4 mr-2" />
            Atrakinti turinį
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Registruokitės nemokamai
          </p>
        </div>
        
        {/* Hidden background content */}
        <div className="opacity-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-primary">{icon}</div>
            <div>
              <h3 className="font-semibold text-lg text-foreground">{title}</h3>
            </div>
          </div>
          <div className="h-32 bg-secondary/20 rounded"></div>
        </div>
      </Card>
    );
  };

  // Toggle card expansion
  const toggleCardExpansion = (cardKey) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardKey]: !prev[cardKey]
    }));
  };

  const renderNumberCard = (number, title, icon, description, calculationExplanation, cardKey, category = null) => {
    // Use category-specific description if available
    const desc = category ? getCategoryDescription(category, number) : NUMBER_DESCRIPTIONS[number];
    const baseDesc = NUMBER_DESCRIPTIONS[number]; // For tabs that still use base descriptions
    if (!desc || !baseDesc) return null;

    const fullText = `${desc.title}\n\n${desc.description || baseDesc.character}\n\n${baseDesc.financial}\n\n${baseDesc.relationships}\n\n${baseDesc.advice}`;
    const isExpanded = expandedCards[cardKey] ?? true; // Default to expanded

    return (
      <Card 
        className={`card-mystical p-6 shadow-card border-border/50 h-full flex flex-col transition-all duration-300 ${!isExpanded ? 'cursor-pointer hover:border-primary/50' : ''}`}
        data-testid={`number-card-${cardKey}`}
      >
        {/* Header - always visible and clickable */}
        <div 
          className="flex items-start justify-between mb-4 cursor-pointer"
          onClick={() => toggleCardExpansion(cardKey)}
          data-testid={`card-header-${cardKey}`}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="text-primary">
              {icon}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-3xl font-display text-primary font-bold">
              {number}
            </div>
            {/* Expand/Collapse button */}
            <button 
              className="p-1 rounded-full hover:bg-primary/10 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                toggleCardExpansion(cardKey);
              }}
              data-testid={`card-toggle-${cardKey}`}
            >
              {isExpanded ? (
                <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground hover:text-foreground" />
              )}
            </button>
          </div>
        </div>
        
        {/* Collapsible content */}
        {isExpanded && (
          <div className="space-y-4 flex-1 animate-in fade-in duration-200">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-foreground">{desc.title}</h4>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(fullText, title);
                    }}
                    className="text-primary hover:text-primary-glow hover:bg-primary/10"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {calculationExplanation && (
                    <CalculationDialog 
                      title={title} 
                      explanation={calculationExplanation}
                    />
                  )}
                </div>
              </div>
            </div>
            
            <Tabs defaultValue="character" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-muted/30 h-auto p-1 gap-1">
                <TabsTrigger value="character" className="text-[10px] sm:text-xs px-1 sm:px-2 py-2 whitespace-normal leading-tight">Charakteris</TabsTrigger>
                <TabsTrigger value="financial" className="text-[10px] sm:text-xs px-1 sm:px-2 py-2 whitespace-normal leading-tight">Finansai</TabsTrigger>
                <TabsTrigger value="relationships" className="text-[10px] sm:text-xs px-1 sm:px-2 py-2 whitespace-normal leading-tight">Santykiai</TabsTrigger>
                <TabsTrigger value="realize" className="text-[10px] sm:text-xs px-1 sm:px-2 py-2 whitespace-normal leading-tight">Sėkmė</TabsTrigger>
              </TabsList>
              
              <TabsContent value="character" className="mt-4">
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {/* Use category-specific description if available */}
                  {desc.description || baseDesc.character}
                </p>
              </TabsContent>
              
              <TabsContent value="financial" className="mt-4">
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {baseDesc.financial}
                </p>
              </TabsContent>
              
              <TabsContent value="relationships" className="mt-4">
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {baseDesc.relationships}
                </p>
              </TabsContent>
              
              <TabsContent value="realize" className="mt-4">
                {(() => {
                  // Hardcoded success formulas for each number
                  const successFormulas = {
                    1: 'Originalumas + Drąsa + Veiksmas = Jūsų realizacija.',
                    2: 'Empatija + Partnerystė + Kantrybė = Jūsų realizacija.',
                    3: 'Kūrybiškumas + Komunikacija + Džiaugsmas = Jūsų realizacija.',
                    4: 'Disciplina + Sistemingumas + Kantrybė = Jūsų realizacija.',
                    5: 'Laisvė + Prisitaikymas + Drąsa = Jūsų realizacija.',
                    6: 'Rūpestis + Harmonija + Meilė sau = Jūsų realizacija.',
                    7: 'Žinios + Intuicija + Specializacija = Jūsų realizacija.',
                    8: 'Ambicija + Strategija + Integritetas = Jūsų realizacija.',
                    9: 'Užuojauta + Tarnavimas + Meilė sau = Jūsų realizacija.',
                    11: 'Intuicija + Įkvėpimas + Įžeminimas = Jūsų realizacija.',
                    22: 'Vizija + Praktinis veikimas + Kantrybė = Jūsų realizacija.',
                    33: 'Besąlygiška meilė + Mokymas pavyzdžiu + Savęs priežiūra = Jūsų realizacija.',
                    44: 'Globali vizija + Sisteminis mąstymas + Komanda = Jūsų realizacija.'
                  };
                  
                  const formula = successFormulas[number] || 'Sėkmė + Darbas + Tikėjimas = Jūsų realizacija.';
                  
                  // Get text without the formula part
                  const mainText = baseDesc.positiveRealization 
                    ? baseDesc.positiveRealization.split('Sėkmės formulė:')[0].trim()
                    : 'Išnaudokite savo unikalią energiją koncentruodamiesi į savo stiprybes ir siekdami teigiamų pokyčių.';
                  
                  return (
                    <div className="text-sm text-foreground/80 leading-relaxed">
                      <p className="mb-3">{mainText}</p>
                      <p className="text-amber-400 font-medium">
                        Sėkmės formulė: {formula}
                      </p>
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Card>
    );
  };

  const dailyAdvice = getDailyAdvice(data.daily);
  const essence = generateEssence(data.lifePath, data.expression, data.soul, data.personality);
  const fullName = `${data.firstName} ${data.lastName}`;

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-foreground/70 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti
            </Button>
            <UserMenu />
          </div>
          
          <div className="text-center mb-6">
            <h1 className="font-display text-3xl sm:text-4xl text-primary mb-3">
              {getGreeting(data.firstName, data.gender)} 🌟
            </h1>
            <p className="text-foreground/80 text-lg mb-1">
              Čia yra jūsų asmeninė numerologinė analizė
            </p>
            <p className="text-muted-foreground text-sm">
              Gimimo data: {data.day}/{data.month}/{data.year}
            </p>
          </div>
        </div>

        {/* Tavo Esmė Section */}
        <Card className="card-mystical p-8 mb-8 border-primary/30 shadow-glow">
          <div className="flex items-start gap-4">
            <Sparkles className="w-10 h-10 text-primary flex-shrink-0 animate-pulse-glow" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-2xl text-primary">Tavo Esmė</h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(essence, 'Tavo Esmė')}
                  className="text-primary hover:text-primary-glow hover:bg-primary/10"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-foreground/90 text-base leading-relaxed">
                {essence}
              </p>
            </div>
          </div>
        </Card>

        {/* Numbers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Life Path - ALWAYS visible for everyone (all 4 tabs) */}
          {renderNumberCard(
            data.lifePath,
            'Gyvenimo kelio skaičius',
            <Star className="w-6 h-6" />,
            'Jūsų pagrindinė misija',
            explainLifePath(data.day, data.month, data.year),
            'lifePath',
            'lifePath'  // Category for specific description
          )}
          
          {/* Expression - LOCKED for guests */}
          {shouldLockContent ? (
            renderLockedCard(
              'Išraiškos skaičius',
              <Sparkles className="w-6 h-6" />,
              'Kaip pasaulis jus mato'
            )
          ) : (
            renderNumberCard(
              data.expression,
              'Išraiškos skaičius',
              <Sparkles className="w-6 h-6" />,
              'Kaip pasaulis jus mato',
              explainExpression(fullName),
              'expression',
              'expression'  // Category for specific description
            )
          )}
          
          {/* Soul - LOCKED for guests */}
          {shouldLockContent ? (
            renderLockedCard(
              'Sielos skaičius',
              <Heart className="w-6 h-6" />,
              'Jūsų vidiniai troškimai'
            )
          ) : (
            renderNumberCard(
              data.soul,
              'Sielos skaičius',
              <Heart className="w-6 h-6" />,
              'Jūsų vidiniai troškimai',
              explainSoul(fullName),
              'soul',
              'soulUrge'  // Category for specific description
            )
          )}
          
          {/* Personality - LOCKED for guests */}
          {shouldLockContent ? (
            renderLockedCard(
              'Asmenybės skaičius',
              <User className="w-6 h-6" />,
              'Kaip kiti suvokia tavo išorinį įvaizdį'
            )
          ) : (
            renderNumberCard(
              data.personality,
              'Asmenybės skaičius',
              <User className="w-6 h-6" />,
              'Kaip kiti suvokia tavo išorinį įvaizdį',
              explainPersonality(fullName),
              'personality',
              'personality'  // Category for specific description
            )
          )}
        </div>

        {/* Prigimties Archetipas - Kinų Zodiakas */}
        {data.year && (() => {
          const zodiac = calculateChineseZodiac(data.year);
          return (
            <Card className="card-mystical p-6 mb-8 border-primary/30">
              <div className="flex items-start gap-4">
                <div className="text-5xl">{zodiac.emoji}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-display text-xl text-primary flex items-center gap-2">
                        <Compass className="w-5 h-5" />
                        Prigimties Archetipas
                      </h3>
                      <p className="text-lg font-semibold text-foreground">{zodiac.fullTitle}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(
                        `${zodiac.fullTitle}\n\n${zodiac.character}\n\nStiprybes: ${zodiac.strengths}\n\nIššūkiai: ${zodiac.challenges}`,
                        'Prigimties Archetipas'
                      )}
                      className="text-primary hover:text-primary-glow hover:bg-primary/10"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3 text-sm text-foreground/80">
                    <p className="leading-relaxed">{zodiac.character}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border/30">
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">✨ Stiprybės</h4>
                        <p className="text-sm">{zodiac.strengths}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">⚡ Iššūkiai</h4>
                        <p className="text-sm">{zodiac.challenges}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">💼 Karjeros kryptys</h4>
                        <p className="text-sm">{zodiac.career}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">💕 Suderinamumas</h4>
                        <p className="text-sm">{zodiac.compatibility}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })()}

        {/* Sielos Matrica */}
        <div className="mb-8">
          <PythagorasSquare 
            day={data.day}
            month={data.month}
            year={data.year}
            isVIP={isVIP}
            onVIPSuccess={handleVIPSuccess}
            userData={{
              firstName: data.firstName,
              lifePath: data.lifePath
            }}
          />
        </div>

        {/* VIP Block - "Nori pilno savo sielos žemėlapio archyvo?" - ONLY for non-VIP */}
        {!isVIP && (
          <div className="mb-8 max-w-3xl mx-auto">
            <SoulArchiveBlock onVIPClick={() => setShowVIPModal(true)} />
          </div>
        )}

        {/* Email Subscription - ONLY for non-VIP guests */}
        {!isVIP && (
          <div className="mb-8">
            <EmailSubscription />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-3xl mx-auto mb-8">
          <Button
            onClick={() => navigate('/prognozes')}
            size="lg"
            className="bg-primary hover:bg-primary-glow text-primary-foreground font-semibold glow-gold"
          >
            <Sun className="w-5 h-5 mr-2" />
            Tavo Prognozės
          </Button>
          
          <Button
            onClick={() => navigate('/suderinamumas')}
            size="lg"
            className="bg-secondary hover:bg-secondary/80 text-secondary-foreground font-semibold"
          >
            <Heart className="w-5 h-5 mr-2" />
            Santykių harmonija
          </Button>
          
          <Button
            onClick={() => navigate('/skaiciuoti')}
            size="lg"
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Skaičiuoti iš naujo
          </Button>
        </div>

        {/* Daily Energy Analysis - visible for all users, placed between buttons and privacy block */}
        <div className="mb-8">
          <DailyEnergyAnalysis />
        </div>

        {/* VIP Modal */}
        <VIPModal
          isOpen={showVIPModal}
          onClose={() => setShowVIPModal(false)}
          onSuccess={handleVIPSuccess}
          userData={data}
        />

        {/* VIP Success Modal */}
        <VIPSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          userName={data.firstName}
          userGender={data.gender}
        />

        {/* Auth Modal for registration */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setShowAuthModal(false);
            // Clear guest popup timer
            if (guestPopupTimerRef.current) {
              clearTimeout(guestPopupTimerRef.current);
            }
          }}
          defaultTab="register"
        />

        {/* Guest Registration Popup (40 seconds) */}
        <GuestRegistrationPopup 
          isVisible={showGuestPopup}
          isAuthenticated={isAuthenticated}
          onClose={() => setShowGuestPopup(false)}
          onRegister={() => {
            setShowGuestPopup(false);
            setShowAuthModal(true);
          }}
        />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
